// 멀티스텝 견적 설문 (dentaleventhub 570 형식 포팅)
// 0 인트로 → 1~3 선택형 질문 → 4 이름 → 5 연락처 + 동의 + 제출
// 배경 이미지에 질문/진행바가 박혀 있고 버튼·입력칸만 이미지 위에 오버레이

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { handleFormSubmit } from "../handlers/formHandlers";
import { validateName, validatePhoneNumber } from "../utils/validation";
import { prohibitedWords } from "../utils/dummyData";

import "./randingStyle33.scss";
import { buildPrivacyNewFormHtml } from "./privacyNewForm";
import MarketingContent from "./MarketingContent";

const QUESTIONS = [
  { key: "q1", options: ["본인", "가족 혹은 지인"] },
  { key: "q2", options: ["앞니", "어금니", "전체", "모르겠음"] },
  { key: "q3", options: ["20~30대", "40대", "50대", "60대 이상"] },
];

function RandingStyle33({
  urlCode,
  urlCodeSettingId,
  imageUrls, // { intro, start, steps: [02,03,04,05,06], keepGo, submit }
  imageAlts = [],
  companyName,
  privacyCompanyName,
  privacyTrustee,
  footerText,
  footerLegalColor,
  apiUrl,
  kakaotalk,
  carrotTag,
}) {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "" });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isAgreed, setIsAgreed] = useState(false);
  const [isMarketingAgreed, setIsMarketingAgreed] = useState(false);
  const [modal, setModal] = useState(null); // "privacy" | "marketing" | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);

  const nameRef = useRef(null);
  const phoneRef = useRef(null);

  const bgs = [imageUrls.intro, ...imageUrls.steps];

  useEffect(() => {
    bgs.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    [imageUrls.start, imageUrls.keepGo, imageUrls.submit].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    const target = page === 4 ? nameRef : page === 5 ? phoneRef : null;
    if (!target) return;
    const t = setTimeout(() => target.current?.focus({ preventScroll: true }), 250);
    return () => clearTimeout(t);
  }, [page]);

  const phoneNumber = {
    first: phone.slice(0, 3),
    second: phone.slice(3, 7),
    third: phone.slice(7),
  };

  const handleChoice = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setTimeout(() => setPage((p) => p + 1), 220);
  };

  const handleKeepGo = () => {
    if (!name.trim()) {
      alert("이름을 입력해 주세요.");
      nameRef.current?.focus();
      return;
    }
    setPage(5);
  };

  const handleAllAgree = (checked) => {
    setIsAgreed(checked);
    setIsMarketingAgreed(checked);
  };

  const openModal = (e, type) => {
    e.preventDefault();
    e.stopPropagation(); // label 클릭으로 체크박스가 토글되지 않게
    setModal(type);
  };

  const validateInput = () =>
    validateName(name, prohibitedWords) && validatePhoneNumber(phoneNumber);

  const handleSubmit = () => {
    const selectedOption = [answers.q1, answers.q2, answers.q3].filter(Boolean).join(" / ");
    handleFormSubmit({
      name,
      phoneNumber,
      selectedOption,
      isAgreed,
      submitCount,
      setSubmitCount,
      validateInput,
      urlCode,
      urlCodeSettingId,
      navigate,
      setIsSubmitting,
      apiUrl,
      kakaotalk,
      carrotTag,
    });
  };

  const hospital = privacyCompanyName || companyName || "수집주체명";

  return (
    <div className="rs33">
      {/* 0 인트로 */}
      <div className="rs33-page" data-page="0" hidden={page !== 0}>
        <img className="rs33-bg" src={bgs[0]} alt={imageAlts[0] || ""} />
        <button type="button" className="rs33-img-btn rs33-start" onClick={() => setPage(1)}>
          <img src={imageUrls.start} alt="시작하기" />
        </button>
      </div>

      {/* 1~3 선택형 질문 */}
      {QUESTIONS.map((q, i) => (
        <div className="rs33-page" data-page={i + 1} hidden={page !== i + 1} key={q.key}>
          <img className="rs33-bg" src={bgs[i + 1]} alt={imageAlts[i + 1] || ""} />
          <div className="rs33-choices">
            {q.options.map((opt) => (
              <button
                type="button"
                key={opt}
                className={`rs33-choice${answers[q.key] === opt ? " selected" : ""}`}
                onClick={() => handleChoice(q.key, opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* 4 이름 */}
      <div className="rs33-page" data-page="4" hidden={page !== 4}>
        <img className="rs33-bg" src={bgs[4]} alt={imageAlts[4] || ""} />
        <div className="rs33-field">
          <label htmlFor="rs33-name">이름</label>
          <input
            ref={nameRef}
            type="text"
            id="rs33-name"
            placeholder="이름을 입력해주세요"
            autoComplete="name"
            maxLength={20}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleKeepGo();
              }
            }}
          />
        </div>
        <button type="button" className="rs33-img-btn rs33-keepgo" onClick={handleKeepGo}>
          <img src={imageUrls.keepGo} alt="계속하기" />
        </button>
      </div>

      {/* 5 연락처 + 동의 + 제출 */}
      <div className="rs33-page" data-page="5" hidden={page !== 5}>
        <img className="rs33-bg" src={bgs[5]} alt={imageAlts[5] || ""} />
        <div className="rs33-field">
          <label htmlFor="rs33-phone">연락처</label>
          <input
            ref={phoneRef}
            type="tel"
            id="rs33-phone"
            placeholder="연락처를 입력해주세요"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={11}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 11))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>
        <div className="rs33-consent">
          <label className="all">
            <input
              type="checkbox"
              checked={isAgreed && isMarketingAgreed}
              onChange={(e) => handleAllAgree(e.target.checked)}
            />
            전체동의
          </label>
          <div className="rs33-consent-col">
            <label>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={(e) => setIsAgreed(e.target.checked)}
              />
              상담/예약 서비스 제공 동의 (필수)
              <button type="button" className="rs33-detail" onClick={(e) => openModal(e, "privacy")}>
                자세히보기
              </button>
            </label>
            <label>
              <input
                type="checkbox"
                checked={isMarketingAgreed}
                onChange={(e) => setIsMarketingAgreed(e.target.checked)}
              />
              마케팅 정보 수신 (선택)
              <button type="button" className="rs33-detail" onClick={(e) => openModal(e, "marketing")}>
                자세히보기
              </button>
            </label>
          </div>
        </div>
        <button
          type="button"
          className="rs33-img-btn rs33-submit"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          <img src={imageUrls.submit} alt="제출하기" />
        </button>
      </div>

      {footerText && (
        <footer className="rs33-footer">
          {footerLegalColor
            ? footerText.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 && "\n"}
                  {/^(Copyright|사업자등록번호)/.test(line) ? (
                    <span style={{ color: footerLegalColor }}>{line}</span>
                  ) : (
                    line
                  )}
                </React.Fragment>
              ))
            : footerText}
        </footer>
      )}

      {modal && (
        <div
          className="rs33-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(null);
          }}
        >
          <div className="rs33-modal-box" role="dialog" aria-modal="true">
            <div className="rs33-modal-head">
              <span>{modal === "privacy" ? "상담/예약 서비스 제공 동의" : "마케팅 정보 수신 동의"}</span>
              <button
                type="button"
                className="rs33-modal-close"
                aria-label="닫기"
                onClick={() => setModal(null)}
              >
                ×
              </button>
            </div>
            <div className="rs33-modal-body">
              {modal === "privacy" ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: buildPrivacyNewFormHtml({ hospital, trustee: privacyTrustee }),
                  }}
                />
              ) : (
                <div className="rs33-marketing">
                  <MarketingContent companyName={hospital} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RandingStyle33;
